import { Directive, ElementRef, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';

/**
 * Attribute directive to style a DaisyUI timeline <li> item.
 * IMPORTANT: DaisyUI timeline styles target `.timeline-vertical > li`.
 * That means we must NOT wrap the <li> in an Angular component tag.
 */
@Directive({
  selector: 'li[uiTimelineItem]',
  standalone: true,
})
export class UiTimelineItemDirective implements OnInit, OnChanges {
  /**
   * Position of the content box relative to the timeline.
   * 'start' = left side, 'end' = right side.
   */
  @Input() uiTimelineItemPosition: 'start' | 'end' = 'end';

  /** DaisyUI color variant for the connector (<hr>) */
  @Input() uiTimelineItemColor: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error' | 'info' | 'neutral' = 'primary';

  /** Whether this is the first item (no line before) */
  @Input() uiTimelineItemIsFirst = false;

  /** Whether this is the last item (no line after) */
  @Input() uiTimelineItemIsLast = false;

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.apply();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.apply();
  }

  private apply(): void {
    const host = this.el.nativeElement;

    // Remove previously inserted hrs
    host.querySelectorAll('[data-ui-timeline-hr="start"], [data-ui-timeline-hr="end"]').forEach(n => n.remove());

    const hrClass = this.hrColorClass(this.uiTimelineItemColor);

    // Match DaisyUI examples:
    // - first: (no leading) ... <hr />
    // - middle: <hr /> ... <hr />
    // - last: <hr /> ... (no trailing)

    if (!this.uiTimelineItemIsFirst) {
      const hr = this.renderer.createElement('hr');
      this.renderer.setAttribute(hr, 'data-ui-timeline-hr', 'start');
      this.renderer.addClass(hr, hrClass);
      this.renderer.insertBefore(host, hr, host.firstChild);
    }

    if (!this.uiTimelineItemIsLast) {
      const hr = this.renderer.createElement('hr');
      this.renderer.setAttribute(hr, 'data-ui-timeline-hr', 'end');
      this.renderer.addClass(hr, hrClass);
      this.renderer.appendChild(host, hr);
    }
  }

  private hrColorClass(color: string): string {
    const map: Record<string, string> = {
      primary: 'bg-primary',
      secondary: 'bg-secondary',
      accent: 'bg-accent',
      success: 'bg-success',
      warning: 'bg-warning',
      error: 'bg-error',
      info: 'bg-info',
      neutral: 'bg-neutral',
    };
    return map[color] ?? 'bg-primary';
  }
}
